import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ArrowRight, Check, ChevronRight, Mail, Clock, Sparkles, Stethoscope, Activity, Utensils, Wind, Dumbbell, Award, Cpu, Heart } from "lucide-react";

const T = {
  cream: "#F5F1EB",
  ink: "#0F0F0F",
  gold: "#EC6B1F",
  goldSoft: "#F5842B",
  soft: "#4a4a4a",
  line: "#E8E2D5",
};
const serif = { fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif" };
const sans = { fontFamily: "'Inter', -apple-system, sans-serif" };

// Data di inizio corso — modificabile via env REACT_APP_LONGEVITY_START
// Default: 1 Ottobre 2026, 00:00 CET (UTC+2 in DST → 22:00 UTC del 30 settembre)
const START_DATE = new Date(
  process.env.REACT_APP_LONGEVITY_START || "2026-10-01T00:00:00+02:00"
);

const NAV = [
  { label: "Home", href: "/v2" },
  { label: "Il Metodo", href: "/v2/metodo" },
  { label: "Il Percorso", href: "/v2/percorso" },
  { label: "Pacchetti", href: "/v2#pacchetti" },
  { label: "Respirazione", href: "/v2/respirazione" },
  { label: "Per Aziende", href: "/v2/aziende" },
  { label: "Academy", href: "/v2/academy" },
  { label: "FAQ", href: "/v2/faq" },
];

const IMG = {
  // Coach Longevity in polo brand + gruppo al lago — hero autentica
  home: "https://customer-assets-agu9un31.emergentagent.net/job_702651d9-ef70-48a7-aca7-36298387f6de/artifacts/fempxwso_Gemini_Generated_Image_h7hwjoh7hwjoh7hw.jpeg",
  // Mamma che cucina con la bimba — vita reale, nutrizione mediterranea in famiglia
  metodo: "https://customer-assets-agu9un31.emergentagent.net/job_702651d9-ef70-48a7-aca7-36298387f6de/artifacts/y77dr57u_Gemini_Generated_Image_10yzt410yzt410yz.jpeg",
  percorso: "https://customer-assets-agu9un31.emergentagent.net/job_702651d9-ef70-48a7-aca7-36298387f6de/artifacts/5452txj0_f09aa3b2-e8d0-4d86-bb59-bb42dd889180.jpg",
  respiro: "https://customer-assets-agu9un31.emergentagent.net/job_702651d9-ef70-48a7-aca7-36298387f6de/artifacts/couqs0lc_Davide-Carrera-copyright-by-Davide-Carrera.jpg",
  dati: "https://customer-assets-agu9un31.emergentagent.net/job_702651d9-ef70-48a7-aca7-36298387f6de/artifacts/mezsjdf4_ChatGPT%20Image%2013%20ago%202026%2C%2021_41_56.png",
  aziende: "https://customer-assets-agu9un31.emergentagent.net/job_702651d9-ef70-48a7-aca7-36298387f6de/artifacts/f62vue3l_ChatGPT%20Image%2013%20ago%202026%2C%2021_37_06.png",
  // Yoga sulle colline toscane al tramonto — Raduno annuale in Toscana
  toscana: "https://customer-assets-agu9un31.emergentagent.net/job_702651d9-ef70-48a7-aca7-36298387f6de/artifacts/dgirj5oq_ChatGPT%20Image%2024%20ago%202026%2C%2009_17_36.png",
  // Pain points: uomo che mangia junk food + uomo insonne — sezione "forse ti riconosci"
  painFood: "https://customer-assets-agu9un31.emergentagent.net/job_702651d9-ef70-48a7-aca7-36298387f6de/artifacts/pxp8z99s_Gemini_Generated_Image_bqtb8xbqtb8xbqtb.jpeg",
  painSleep: "https://customer-assets-agu9un31.emergentagent.net/job_702651d9-ef70-48a7-aca7-36298387f6de/artifacts/rxuoykxb_Gemini_Generated_Image_j72es0j72es0j72e.jpeg",
};

// ============ ACADEMY LOGO (immagine ufficiale — mix-blend-multiply per sfondo cream) ============
const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_702651d9-ef70-48a7-aca7-36298387f6de/artifacts/yhescrlt_image.png";
const AcademyLogo = ({ height = 40, onDark = false }) => (
  <img
    src={LOGO_URL}
    alt="Longevity Academy Italia"
    style={{
      height,
      width: "auto",
      // multiply: fonde il bianco dello sfondo del logo col beige del sito (bianco * beige = beige)
      // su fondo scuro invertiamo la logica per mantenere leggibilità
      mixBlendMode: onDark ? "screen" : "multiply",
      display: "block",
    }}
  />
);

// ============ SHARED LAYOUT ============
const NavBar = () => {
  const [open, setOpen] = React.useState(false);
  const loc = useLocation();
  return (
    <header className="w-full sticky top-0 z-40 border-b" style={{ backgroundColor: T.cream, borderColor: T.line }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
        <Link to="/v2" className="flex items-center" data-testid="v2-brand">
          <AcademyLogo height={44} />
        </Link>
        <nav className="hidden lg:flex gap-6 items-center">
          {NAV.map((n) => (
            <Link key={n.href} to={n.href} className={`text-sm font-semibold hover:opacity-80 transition-opacity ${loc.pathname === n.href ? "underline underline-offset-4" : ""}`} style={{ color: T.gold }}>{n.label}</Link>
          ))}
          <Link to="/v2/iscriviti" className="inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-widest font-bold" style={{ backgroundColor: T.gold, color: "#fff" }} data-testid="cta-nav-iscriviti">
            Inizia il percorso <ArrowRight className="w-4 h-4" />
          </Link>
        </nav>
        <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn"><Menu style={{ color: T.gold }} /></button>
      </div>
      {open && (
        <div className="lg:hidden px-4 py-4 border-t" style={{ borderColor: T.line, backgroundColor: T.cream }}>
          {NAV.map((n) => (
            <Link key={n.href} to={n.href} onClick={() => setOpen(false)} className="block py-2 text-sm font-semibold" style={{ color: T.gold }}>{n.label}</Link>
          ))}
          <Link to="/v2/iscriviti" onClick={() => setOpen(false)} className="inline-flex items-center gap-2 mt-3 px-4 py-2 text-xs uppercase tracking-widest font-bold" style={{ backgroundColor: T.gold, color: "#fff" }}>
            Inizia il percorso <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="mt-24 py-12 px-4 md:px-8 border-t" style={{ backgroundColor: "#0F0F0F", color: T.cream, borderColor: T.gold }}>
    <div className="max-w-7xl mx-auto text-center">
      <div className="flex items-center justify-center mb-4">
        <AcademyLogo height={40} onDark />
      </div>
      <p className="text-sm italic mb-4" style={serif}>Conosci. Misura. Comprendi. Scegli. Trasforma.</p>
      <p className="text-xs opacity-60">Longevity Academy Italia · info@longevityacademy.it</p>
    </div>
  </footer>
);

const Layout = ({ children, minimal = false }) => (
  <div style={{ backgroundColor: T.cream, color: T.ink, ...sans, minHeight: "100vh" }}>
    {!minimal && <NavBar />}
    {minimal && (
      <div className="w-full py-4 px-4 md:px-8 border-b" style={{ borderColor: T.line, backgroundColor: T.cream }}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/v2" className="flex items-center">
            <AcademyLogo height={40} />
          </Link>
          <Link to="/v2" className="text-xs uppercase tracking-widest" style={{ color: T.soft }}>← Torna al sito</Link>
        </div>
      </div>
    )}
    <main>{children}</main>
    {!minimal && <Footer />}
  </div>
);

// ============ REUSABLE ============
const Section = ({ children, dark = false, className = "", ...p }) => (
  <section className={`px-4 md:px-8 py-16 md:py-24 ${className}`} style={{ backgroundColor: dark ? "#0F0F0F" : T.cream, color: dark ? T.cream : T.ink }} {...p}>
    <div className="max-w-6xl mx-auto">{children}</div>
  </section>
);

const H1 = ({ children }) => <h1 className="text-4xl md:text-6xl font-light leading-[1.1] mb-6" style={serif}>{children}</h1>;
const H2 = ({ children }) => <h2 className="text-3xl md:text-5xl font-light leading-tight mb-6" style={serif}>{children}</h2>;
const Eyebrow = ({ children }) => <p className="text-xs tracking-[4px] uppercase mb-4 font-bold" style={{ color: T.gold }}>{children}</p>;
const P = ({ children, className = "" }) => <p className={`text-base md:text-lg leading-relaxed mb-4 ${className}`}>{children}</p>;
const CtaPrimary = ({ href = "/v2/iscriviti", children = "Inizia il percorso" }) => (
  <Link to={href} className="inline-flex items-center gap-3 px-7 py-4 text-xs md:text-sm tracking-[2px] uppercase font-bold shadow-lg hover:scale-[1.02] transition-transform" style={{ backgroundColor: T.gold, color: "#fff" }}>
    {children} <ArrowRight className="w-4 h-4" />
  </Link>
);

// ============ COUNTDOWN BANNER (urgenza 1 Ottobre) ============
const useCountdown = (target) => {
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now.getTime());
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const mins = Math.floor((diff / (1000 * 60)) % 60);
  const secs = Math.floor((diff / 1000) % 60);
  return { days, hours, mins, secs, expired: diff === 0 };
};

const CountdownTile = ({ value, label }) => (
  <div className="flex flex-col items-center min-w-[68px] md:min-w-[92px]">
    <div
      className="text-3xl md:text-5xl font-light tabular-nums leading-none px-3 py-3 md:px-5 md:py-4 border-2 shadow-lg"
      style={{ borderColor: T.gold, backgroundColor: "rgba(15,15,15,0.85)", color: "#fff", ...serif }}
      data-testid={`countdown-${label.toLowerCase()}`}
    >
      {String(value).padStart(2, "0")}
    </div>
    <span className="text-[10px] md:text-xs uppercase tracking-[3px] mt-2 font-bold" style={{ color: T.goldSoft }}>{label}</span>
  </div>
);

const CountdownBanner = () => {
  const { days, hours, mins, secs, expired } = useCountdown(START_DATE);
  const dateLabel = START_DATE.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Rome" });
  return (
    <div
      className="relative overflow-hidden border-y"
      style={{
        background: `linear-gradient(135deg, #0F0F0F 0%, #1a1a1a 55%, #2a1608 100%)`,
        borderColor: T.gold,
        color: "#fff",
      }}
      data-testid="urgency-countdown-banner"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{ background: `radial-gradient(circle at 20% 20%, ${T.gold}55 0%, transparent 45%), radial-gradient(circle at 80% 80%, ${T.goldSoft}44 0%, transparent 50%)` }}
      />
      <div className="relative max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12 grid md:grid-cols-2 gap-8 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 border" style={{ borderColor: T.gold, backgroundColor: "rgba(236,107,31,0.12)" }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: T.goldSoft }} />
            <span className="text-[11px] uppercase tracking-[3px] font-bold" style={{ color: T.goldSoft }}>Posti limitati</span>
          </div>
          <h3 className="text-2xl md:text-4xl font-light leading-tight mb-3" style={serif}>
            I corsi iniziano il <em style={{ color: T.goldSoft, fontStyle: "italic" }}>{dateLabel}</em>.
          </h3>
          <p className="text-sm md:text-base opacity-85 mb-4" style={{ maxWidth: 480 }}>
            Ogni edizione accoglie un numero limitato di partecipanti per garantire qualità e follow-up.
            <span className="hidden md:inline"> </span>
            <strong className="block md:inline mt-1 md:mt-0" style={{ color: T.goldSoft }}>Affrettati alle iscrizioni.</strong>
          </p>
          <Link to="/v2/iscriviti" className="inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[2px] font-bold" style={{ backgroundColor: T.gold, color: "#fff" }} data-testid="urgency-cta-iscriviti">
            <Clock className="w-4 h-4" /> Prenota il tuo posto
          </Link>
        </div>
        <div className="flex justify-center md:justify-end">
          {expired ? (
            <p className="text-lg italic" style={{ ...serif, color: T.goldSoft }}>Le iscrizioni sono aperte — contattaci!</p>
          ) : (
            <div className="flex gap-2 md:gap-4">
              <CountdownTile value={days} label="Giorni" />
              <CountdownTile value={hours} label="Ore" />
              <CountdownTile value={mins} label="Min" />
              <CountdownTile value={secs} label="Sec" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ LEAD CAPTURE — PDF DETTAGLI ACADEMY ============
const PdfLeadCapture = () => {
  const [email, setEmail] = React.useState("");
  const [name, setName] = React.useState("");
  const [status, setStatus] = React.useState("idle"); // idle | loading | success | error
  const [msg, setMsg] = React.useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { setStatus("error"); setMsg("Inserisci un'email valida"); return; }
    setStatus("loading"); setMsg("");
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const r = await fetch(`${base}/api/landing/pdf-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, source: "longevity-v2-home" }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Errore invio");
      setStatus("success");
      setMsg(data.email_sent
        ? "Perfetto! Controlla la tua casella: ti abbiamo appena inviato il PDF."
        : "Grazie! La tua richiesta è stata registrata. Ti invieremo il PDF a breve.");
      setEmail(""); setName("");
    } catch (err) {
      setStatus("error");
      setMsg(err.message || "Errore, riprova più tardi");
    }
  };
  return (
    <Section className="!py-16 md:!py-20">
      <div className="grid md:grid-cols-5 gap-8 items-center">
        <div className="md:col-span-2">
          <Eyebrow>Materiali di approfondimento</Eyebrow>
          <H2>Vuoi ricevere il <em style={{ fontStyle: "italic", color: T.gold }}>PDF con i dettagli dell&apos;Academy?</em></H2>
          <P className="text-sm md:text-base" >Metodo, moduli, calendario e team scientifico in un unico documento. Te lo mandiamo direttamente in email.</P>
        </div>
        <div className="md:col-span-3">
          <form
            onSubmit={submit}
            className="p-6 md:p-8 shadow-2xl border-t-4 relative overflow-hidden"
            style={{ backgroundColor: "#fff", borderColor: T.gold }}
            data-testid="pdf-lead-form"
          >
            <div aria-hidden className="absolute -top-8 -right-8 w-28 h-28 rounded-full opacity-15" style={{ backgroundColor: T.gold }} />
            <div className="flex items-center gap-2 mb-4">
              <Mail className="w-5 h-5" style={{ color: T.gold }} />
              <span className="text-xs uppercase tracking-[3px] font-bold" style={{ color: T.gold }}>Richiedi il PDF</span>
            </div>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Nome (opzionale)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border text-sm focus:outline-none focus:border-orange-500"
                style={{ borderColor: T.line, backgroundColor: T.cream }}
                data-testid="pdf-lead-name"
              />
              <input
                required
                type="email"
                placeholder="La tua email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border text-sm focus:outline-none focus:border-orange-500"
                style={{ borderColor: T.line, backgroundColor: T.cream }}
                data-testid="pdf-lead-email"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-xs uppercase tracking-[3px] font-bold disabled:opacity-60 hover:scale-[1.01] transition-transform"
                style={{ backgroundColor: T.gold, color: "#fff" }}
                data-testid="pdf-lead-submit"
              >
                {status === "loading" ? "Invio…" : "Ricevi il PDF"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            {msg && (
              <p
                className={`mt-4 text-sm ${status === "success" ? "" : "italic"}`}
                style={{ color: status === "success" ? "#1d7a1d" : status === "error" ? "#b03a2e" : T.soft }}
                data-testid="pdf-lead-message"
              >
                {msg}
              </p>
            )}
            <p className="text-[11px] mt-4 opacity-60 italic">I tuoi dati sono usati solo per inviarti il PDF e informazioni sull&apos;Academy. Nessuno spam.</p>
          </form>
        </div>
      </div>
    </Section>
  );
};

// ============ PAGES ============
export const HomeV2 = () => (
  <Layout>
    <CountdownBanner />
    <Section className="!py-20 md:!py-32">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div>
          <Eyebrow>Longevity Academy Italia</Eyebrow>
          <H1>Non ti serve un&apos;altra dieta.<br /><em style={{ color: T.gold, fontStyle: "italic" }}>Ti serve capire come funziona il tuo corpo.</em></H1>
          <P>Hai provato diete, programmi di allenamento, consigli trovati online. Per qualche settimana hai funzionato. Poi sei tornato alle vecchie abitudini.</P>
          <P>Non è necessariamente un problema di volontà. Forse nessuno ti ha mai insegnato a capire cosa succede davvero dentro il tuo corpo quando mangi, respiri, ti muovi, ti alleni, dormi e vivi sotto stress.</P>
          <P><strong>Longevity Academy ti insegna a leggere il tuo corpo attraverso la scienza, i dati e la consapevolezza.</strong></P>
          <p className="text-xl mb-8" style={{ color: T.gold, ...serif }}>8 settimane. 2 lezioni live a settimana. Un metodo che impari a costruire tu.</p>
          <div className="flex flex-wrap gap-4">
            <CtaPrimary href="/v2/percorso" data-testid="home-cta-percorso">Scopri il percorso</CtaPrimary>
            <Link to="/v2/metodo" className="inline-flex items-center gap-2 px-6 py-4 text-xs md:text-sm tracking-widest uppercase font-bold border-2" style={{ borderColor: T.ink, color: T.ink }}>Come funziona</Link>
          </div>
        </div>
        <div className="relative">
          <img src={IMG.home} alt="Coach Longevity Academy con gruppo in attività outdoor sul lago" className="w-full h-auto shadow-2xl" />
          <div className="absolute -bottom-4 -left-4 w-24 h-24" style={{ backgroundColor: T.gold }} />
        </div>
      </div>
    </Section>

    <Section dark>
      <div className="grid lg:grid-cols-5 gap-10 items-center">
        <div className="lg:col-span-3">
          <Eyebrow>Forse ti riconosci</Eyebrow>
          <H2>Hai tra i 40 e i 60 anni?<br /><em style={{ color: T.goldSoft, fontStyle: "italic" }}>Lavori. Hai una famiglia. Hai poco tempo.</em></H2>
          <P className="opacity-80">Cerchi di mangiare bene e magari provi anche a fare attività fisica quando riesci. Eppure qualcosa non torna.</P>
          <ul className="space-y-2 my-6 opacity-85">
            {["Ti senti spesso stanco.","La tua energia cala durante la giornata.","Il sonno non ti sembra realmente rigenerante.","Hai difficoltà a mantenere la concentrazione.","Il peso tende ad aumentare, soprattutto nella zona addominale.","Ti senti meno in forma di qualche anno fa.","Ti muovi poco e non sai da dove cominciare.","Hai provato diversi programmi senza riuscire a mantenerli nel tempo."].map((t,i)=>(
              <li key={i} className="flex gap-3"><ChevronRight className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: T.gold }} /><span>{t}</span></li>
            ))}
          </ul>
          <P className="opacity-80">E continui a chiederti: &quot;Cosa sto sbagliando?&quot;</P>
        </div>
        <div className="lg:col-span-2 grid grid-cols-2 gap-3 lg:gap-4">
          <figure className="relative overflow-hidden col-span-2 group">
            <img src={IMG.painSleep} alt="Uomo di mezza età sveglio nella notte — sonno non rigenerante" className="w-full h-56 md:h-64 object-cover group-hover:scale-[1.03] transition-transform duration-700" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,15,15,0) 40%, rgba(15,15,15,0.75) 100%)" }} />
            <figcaption className="absolute bottom-3 left-4 text-xs uppercase tracking-[3px] font-bold" style={{ color: T.goldSoft }}>Sonno · 03:17</figcaption>
          </figure>
          <figure className="relative overflow-hidden group">
            <img src={IMG.painFood} alt="Uomo che mangia junk food — alimentazione fuori equilibrio" className="w-full h-40 md:h-48 object-cover group-hover:scale-[1.03] transition-transform duration-700" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,15,15,0) 40%, rgba(15,15,15,0.75) 100%)" }} />
            <figcaption className="absolute bottom-2 left-3 text-[10px] uppercase tracking-[2px] font-bold" style={{ color: T.goldSoft }}>Cibo</figcaption>
          </figure>
          <figure className="relative overflow-hidden group">
            <img src={IMG.metodo} alt="Mamma in cucina con la bambina — vita reale in famiglia" className="w-full h-40 md:h-48 object-cover group-hover:scale-[1.03] transition-transform duration-700" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,15,15,0) 40%, rgba(15,15,15,0.75) 100%)" }} />
            <figcaption className="absolute bottom-2 left-3 text-[10px] uppercase tracking-[2px] font-bold" style={{ color: T.goldSoft }}>Famiglia</figcaption>
          </figure>
        </div>
      </div>
      <p className="text-2xl md:text-3xl mt-10 italic text-center" style={{ ...serif, color: T.goldSoft }}>Forse la domanda da porsi è un&apos;altra:<br />&quot;Quanto conosco davvero il mio corpo?&quot;</p>
    </Section>

    {/* ============ PACCHETTI: BASIC + PLUS ============ */}
    <Section className="!py-20 md:!py-28" id="pacchetti">
      <div className="text-center mb-14">
        <Eyebrow>Pacchetti</Eyebrow>
        <H2>Scegli il tuo <em style={{ fontStyle: "italic", color: T.gold }}>percorso.</em></H2>
        <p className="max-w-2xl mx-auto text-base md:text-lg" style={{ color: T.soft }}>
          Due formati, un solo metodo. <strong style={{ color: T.ink }}>BASIC</strong> per comprendere e cambiare stile di vita.
          <strong style={{ color: T.ink }}> PLUS</strong> aggiunge misurazioni cliniche personali e biofeedback.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto items-stretch">
        {/* ==== BASIC ==== */}
        <div className="relative flex flex-col shadow-2xl border-t-4" style={{ backgroundColor: "#fff", borderColor: T.gold }} data-testid="package-basic">
          <div className="p-8 md:p-10 flex-1 flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: T.gold }} />
              <span className="text-xs uppercase tracking-[3px] font-bold" style={{ color: T.gold }}>Basic</span>
            </div>
            <h3 className="text-3xl md:text-4xl font-light mb-2" style={{ ...serif, color: T.ink }}>Comprendere<br />e trasformare</h3>
            <p className="text-sm mb-6" style={{ color: T.soft }}>8 settimane per comprendere e migliorare il tuo stile di vita.</p>

            <div className="flex items-baseline gap-2 mb-6 pb-6 border-b" style={{ borderColor: T.line }}>
              <span className="text-5xl font-light tabular-nums" style={{ ...serif, color: T.ink }}>€300</span>
              <span className="text-sm" style={{ color: T.soft }}>una tantum</span>
            </div>

            <ul className="space-y-2 mb-6 text-sm" style={{ color: T.soft }}>
              <li className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: T.gold }} /><span><strong style={{ color: T.ink }}>8 settimane</strong> di formazione</span></li>
              <li className="flex items-start gap-2"><Activity className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: T.gold }} /><span><strong style={{ color: T.ink }}>2 incontri live</strong> a settimana · 45 min</span></li>
              <li className="flex items-start gap-2"><Award className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: T.gold }} /><span>Materiale didattico + <strong style={{ color: T.ink }}>Diploma Longevity Academy</strong></span></li>
            </ul>

            <p className="text-[11px] uppercase tracking-[2px] font-bold mb-3" style={{ color: T.gold }}>I tre pilastri</p>
            <div className="space-y-3 mb-6">
              <div className="p-3 border-l-2" style={{ borderColor: T.gold, backgroundColor: T.cream }}>
                <div className="flex items-center gap-2 mb-1"><Utensils className="w-4 h-4" style={{ color: T.gold }} /><strong className="text-sm" style={{ color: T.ink }}>Alimentazione &amp; Metabolismo</strong></div>
                <p className="text-xs" style={{ color: T.soft }}>Metabolismo, dieta mediterranea, glicemia/insulina, fame &amp; sazietà, comportamento alimentare.</p>
              </div>
              <div className="p-3 border-l-2" style={{ borderColor: T.gold, backgroundColor: T.cream }}>
                <div className="flex items-center gap-2 mb-1"><Wind className="w-4 h-4" style={{ color: T.gold }} /><strong className="text-sm" style={{ color: T.ink }}>Respirazione &amp; Stress</strong></div>
                <p className="text-xs" style={{ color: T.soft }}>Fisiologia dello stress, SNA, tecniche di respirazione, autoregolazione, recupero.</p>
              </div>
              <div className="p-3 border-l-2" style={{ borderColor: T.gold, backgroundColor: T.cream }}>
                <div className="flex items-center gap-2 mb-1"><Dumbbell className="w-4 h-4" style={{ color: T.gold }} /><strong className="text-sm" style={{ color: T.ink }}>Bio-Fitness &amp; Movimento</strong></div>
                <p className="text-xs" style={{ color: T.soft }}>Fisiologia del movimento, ruolo del muscolo, allenamento, adattamento, recupero.</p>
              </div>
            </div>

            <p className="text-[11px] italic mb-6" style={{ color: T.soft }}>
              <span className="inline-block px-2 py-0.5 mr-1" style={{ backgroundColor: T.cream, color: T.soft }}>Opzionale</span>
              ANS Control acquistabile separatamente come strumento personale di biofeedback.
            </p>

            <Link to="/v2/iscriviti?p=basic" className="mt-auto inline-flex items-center justify-center gap-2 w-full px-6 py-3.5 text-xs uppercase tracking-[3px] font-bold border-2 hover:bg-black hover:text-white transition-colors" style={{ borderColor: T.ink, color: T.ink }} data-testid="basic-cta">
              Scegli Basic <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* ==== PLUS ==== */}
        <div className="relative flex flex-col shadow-2xl overflow-hidden" style={{ background: "linear-gradient(160deg, #0F0F0F 0%, #1a0f08 100%)", color: T.cream }} data-testid="package-plus">
          <div className="absolute top-4 right-4 px-3 py-1 text-[10px] uppercase tracking-[3px] font-bold" style={{ backgroundColor: T.gold, color: "#fff" }}>Consigliato</div>
          <div aria-hidden className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${T.gold} 0%, transparent 70%)` }} />
          <div className="relative p-8 md:p-10 flex-1 flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: T.goldSoft }} />
              <span className="text-xs uppercase tracking-[3px] font-bold" style={{ color: T.goldSoft }}>Plus</span>
            </div>
            <h3 className="text-3xl md:text-4xl font-light mb-2" style={serif}>Comprendere,<br /><em style={{ color: T.goldSoft, fontStyle: "italic" }}>misurare, adattare</em></h3>
            <p className="text-sm mb-6 opacity-80">Il percorso Basic + valutazione clinica individuale, biofeedback e monitoraggio.</p>

            <div className="flex items-baseline gap-2 mb-6 pb-6 border-b" style={{ borderColor: "rgba(245,241,235,0.15)" }}>
              <span className="text-5xl font-light tabular-nums" style={{ ...serif, color: T.goldSoft }}>€590</span>
              <span className="text-sm opacity-70">una tantum</span>
            </div>

            <p className="text-[11px] uppercase tracking-[2px] font-bold mb-4" style={{ color: T.goldSoft }}>Tutto il Basic + in più:</p>

            <div className="space-y-4 mb-6">
              <div className="flex gap-3">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center border" style={{ borderColor: T.gold }}>
                  <Cpu className="w-5 h-5" style={{ color: T.goldSoft }} />
                </div>
                <div>
                  <strong className="text-sm block mb-0.5" style={{ color: T.goldSoft }}>ANS Control</strong>
                  <p className="text-xs opacity-85">Biofeedback personale. Esercizi monitorati su <em>respirazione, HRV, reattività cognitiva</em>. Impari a leggere la tua risposta fisiologica.</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center border" style={{ borderColor: T.gold }}>
                  <Stethoscope className="w-5 h-5" style={{ color: T.goldSoft }} />
                </div>
                <div>
                  <strong className="text-sm block mb-0.5" style={{ color: T.goldSoft }}>Visita col Dott. Jacopo Querci</strong>
                  <span className="text-[10px] uppercase tracking-[2px] opacity-70 block mb-1">Studio di Livorno</span>
                  <ul className="text-xs opacity-85 space-y-0.5">
                    <li>· Valutazione del sistema nervoso autonomo</li>
                    <li>· Coerenza cardiaca e respiratoria (RSA)</li>
                    <li>· Infiammazione di basso grado</li>
                    <li>· Adattamento psico-neuro-immunologico</li>
                    <li>· Asse corteccia prefrontale–talamo</li>
                    <li>· Bioimpedenziometria molecolare</li>
                  </ul>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center border" style={{ borderColor: T.gold }}>
                  <Heart className="w-5 h-5" style={{ color: T.goldSoft }} />
                </div>
                <div>
                  <strong className="text-sm block mb-0.5" style={{ color: T.goldSoft }}>VO₂ Max <span className="text-[10px] opacity-70 uppercase tracking-[2px] ml-1">per sportivi</span></strong>
                  <p className="text-xs opacity-85">Valutazione della fitness cardiorespiratoria tramite <strong>PNOĒ</strong> e parametri metabolici correlati.</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center border" style={{ borderColor: T.gold }}>
                  <Sparkles className="w-5 h-5" style={{ color: T.goldSoft }} />
                </div>
                <div>
                  <strong className="text-sm block mb-0.5" style={{ color: T.goldSoft }}>Esercizi quotidiani</strong>
                  <p className="text-xs opacity-85">Pratiche giornaliere durante le 8 settimane sui tre pilastri: <em>alimentazione → respirazione → movimento</em>.</p>
                </div>
              </div>
            </div>

            <Link to="/v2/iscriviti?p=plus" className="mt-auto inline-flex items-center justify-center gap-2 w-full px-6 py-3.5 text-xs uppercase tracking-[3px] font-bold hover:scale-[1.02] transition-transform" style={{ backgroundColor: T.gold, color: "#fff" }} data-testid="plus-cta">
              Scegli Plus <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      <p className="text-center text-xs italic mt-8 max-w-2xl mx-auto" style={{ color: T.soft }}>
        Entrambi i pacchetti includono l&apos;accesso gratuito al <strong>Raduno annuale in Toscana</strong> (escluse spese di vitto e alloggio).
      </p>
    </Section>

    <Section>
      <Eyebrow>Scopri il sito</Eyebrow>
      <H2>Un metodo, <em style={{ fontStyle: "italic", color: T.gold }}>quattro porte d&apos;ingresso.</em></H2>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
        {[
          {t:"Il Metodo", d:"Sei grandi aree, un solo obiettivo: capire il tuo corpo.", h:"/v2/metodo"},
          {t:"Il Percorso", d:"8 settimane, 2 incontri live a settimana, un metodo che costruisci tu.", h:"/v2/percorso"},
          {t:"I Dati", d:"Il tuo corpo produce dati. Impara a leggerli.", h:"/v2/dati"},
          {t:"Per Aziende", d:"La salute delle persone è anche una risorsa per l'azienda.", h:"/v2/aziende"},
        ].map((c,i) => (
          <Link key={i} to={c.h} className="p-6 bg-white border-t-4 shadow hover:shadow-2xl hover:-translate-y-1 transition-all" style={{ borderColor: T.gold }} data-testid={`teaser-${i}`}>
            <h3 className="text-xl mb-2 font-semibold" style={{ ...serif, color: T.ink }}>{c.t}</h3>
            <p className="text-sm" style={{ color: T.soft }}>{c.d}</p>
            <span className="text-xs uppercase tracking-widest mt-4 inline-flex items-center gap-1 font-bold" style={{ color: T.gold }}>Scopri <ArrowRight className="w-3 h-3" /></span>
          </Link>
        ))}
      </div>
      <div className="mt-16 text-center">
        <CtaPrimary />
      </div>
    </Section>

    <PdfLeadCapture />
  </Layout>
);

export const MetodoV2 = () => (
  <Layout>
    <Section className="!py-16 md:!py-24">
      <Eyebrow>Il Metodo</Eyebrow>
      <H1>Il tuo corpo non è un problema da correggere. <em style={{ color: T.gold, fontStyle: "italic" }}>È un sistema da comprendere.</em></H1>
      <div className="grid md:grid-cols-2 gap-10 mt-10 items-start">
        <div>
          <P>Il corpo non funziona a compartimenti separati. Metabolismo, energia, massa muscolare, respirazione e sistema nervoso sono parti di un unico sistema che comunica costantemente con sé stesso.</P>
          <P>Quando qualcosa non funziona — stanchezza, insonnia, peso che aumenta, ansia, difficoltà di concentrazione — non è un problema isolato: è un segnale che qualcosa nel sistema è fuori equilibrio.</P>
          <P>Longevity Academy ti aiuta a comprendere questi segnali attraverso <strong>sei grandi aree</strong>, integrate in un unico metodo.</P>
        </div>
        <div><img src={IMG.metodo} alt="Vita reale in famiglia — la nutrizione parte dalla cucina di tutti i giorni" className="w-full shadow-2xl" /></div>
      </div>
    </Section>

    <Section dark>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          {i:"🧬", t:"Metabolismo", d:"Come funziona la centrale energetica del tuo corpo. Il metabolismo basale, l'energia consumata a riposo, il ruolo dei mitocondri, l'insulino-resistenza."},
          {i:"🥗", t:"Nutrizione", d:"Non un piano da eseguire, ma una lettura della tua alimentazione. Macronutrienti, cronobiologia, indice e carico glicemico, alimentazione mediterranea."},
          {i:"🏃", t:"Attività fisica", d:"Il muscolo è il tuo alleato più sottovalutato. Movimento naturale, HIIT, postura, come costruire massa magra a ogni età."},
          {i:"🫁", t:"Respirazione", d:"Il respiro come regolatore del sistema nervoso. Tecniche guidate ispirate al mondo dell'apnea, applicate alla vita quotidiana."},
          {i:"🧠", t:"Stress", d:"L'asse ormonale del cortisolo, sonno, recupero. Come lo stress cronico altera metabolismo, peso e concentrazione."},
          {i:"📊", t:"Dati", d:"VO2Max, variabilità cardiaca (HRV), composizione corporea, età biologica. Misurare per comprendere, non per ossessionarsi."},
        ].map((a,i)=>(
          <div key={i} className="p-8 bg-white/5 backdrop-blur-sm border" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
            <div className="text-4xl mb-4">{a.i}</div>
            <h3 className="text-xl mb-3 font-semibold" style={{ ...serif, color: T.goldSoft }}>{a.t}</h3>
            <p className="text-sm opacity-80">{a.d}</p>
          </div>
        ))}
      </div>
      <div className="mt-16 text-center"><CtaPrimary /></div>
    </Section>
  </Layout>
);

export const PercorsoV2 = () => (
  <Layout>
    <Section>
      <Eyebrow>Il Percorso</Eyebrow>
      <H1>8 settimane per imparare a <em style={{ color: T.gold, fontStyle: "italic" }}>leggere il tuo corpo.</em></H1>
      <P className="max-w-3xl">2 incontri live a settimana, 45 minuti ciascuno. Un percorso progressivo: prima capisci, poi misuri, poi trasformi.</P>
      <div className="space-y-4 mt-10">
        {[
          {n:"01",t:"Settimane 1-2 · Fondamenta",d:"Metabolismo e nutrizione: come funziona il tuo corpo, come mangi davvero, primi dati di partenza."},
          {n:"02",t:"Settimane 3-4 · Movimento",d:"Il muscolo, l'attività fisica personalizzata per età, il protocollo HIIT da 30 minuti."},
          {n:"03",t:"Settimane 5-6 · Respiro & Stress",d:"Regolazione del sistema nervoso, tecniche di respirazione, sonno e recupero."},
          {n:"04",t:"Settimane 7-8 · Integrazione",d:"Metti tutto insieme: rileggi i tuoi dati, costruisci il tuo metodo, prepara il proseguo autonomo."},
        ].map((f,i)=>(
          <div key={i} className="flex gap-6 p-6 bg-white shadow border-l-4" style={{ borderColor: T.gold }}>
            <div className="text-5xl font-light" style={{ color: T.goldSoft, ...serif }}>{f.n}</div>
            <div>
              <h3 className="text-xl mb-2 font-semibold" style={serif}>{f.t}</h3>
              <p className="text-sm" style={{ color: T.soft }}>{f.d}</p>
            </div>
          </div>
        ))}
      </div>
    </Section>

    <Section dark>
      <Eyebrow>Costruisci il tuo percorso</Eyebrow>
      <H2>Non un piano da eseguire.<br /><em style={{ color: T.goldSoft, fontStyle: "italic" }}>Un metodo che costruisci tu.</em></H2>
      <P className="opacity-80 max-w-3xl">Ogni settimana lavori sul tuo caso reale, con supporto scientifico e coaching. Al termine hai un metodo tuo — non un piano copiato che dimenticherai in due mesi.</P>
    </Section>

    <Section>
      <Eyebrow>A chi è rivolta</Eyebrow>
      <H2>Se ti riconosci qui, <em style={{ fontStyle: "italic", color: T.gold }}>questo percorso è per te.</em></H2>
      <ul className="grid md:grid-cols-2 gap-4 mt-8">
        {["Non sei una persona sportiva — meglio, partiamo davvero dalle fondamenta.","Hai poco tempo — ci sono solo 2 incontri live a settimana, 45 minuti ciascuno.","Hai già provato tante cose — qui capirai perché finora non ha funzionato.","Sei sano ma vuoi capire meglio il tuo corpo per invecchiare bene.","Hai piccoli segnali (stanchezza, sonno, peso) che vuoi capire prima che diventino problemi.","Vuoi un metodo tuo, non un pacchetto standard.",].map((p,i)=>(
          <li key={i} className="flex gap-3 p-5 bg-white shadow"><Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: T.gold }} /><span className="text-sm">{p}</span></li>
        ))}
      </ul>
      <div className="mt-14 text-center"><CtaPrimary /></div>
    </Section>
  </Layout>
);

export const RespirazioneV2 = () => (
  <Layout>
    <Section>
      <Eyebrow>Respirazione</Eyebrow>
      <H1>Il respiro: <em style={{ color: T.gold, fontStyle: "italic" }}>il ponte tra corpo e mente.</em></H1>
      <div className="grid md:grid-cols-2 gap-12 items-center mt-10">
        <div>
          <P>Il respiro è l&apos;unico gesto involontario che puoi anche controllare a volontà. È il ponte tra il corpo e la mente, tra il sistema nervoso simpatico e parasimpatico.</P>
          <P>Quando impari a respirare in modo consapevole, cambi in pochi minuti la tua fisiologia: frequenza cardiaca, cortisolo, chiarezza mentale. Non è meditazione new-age: è fisiologia applicata.</P>
          <P>Il modulo di respirazione di Longevity Academy nasce dall&apos;esperienza di <strong>Davide Carrera</strong>, campione del mondo di apnea, e applica le sue tecniche non al mondo agonistico ma alla vita quotidiana: stress lavorativo, sonno, concentrazione, gestione dell&apos;ansia.</P>
        </div>
        <div>
          <img src={IMG.respiro} alt="Davide Carrera, coach di respirazione e campione del mondo di apnea" className="w-full shadow-2xl" />
        </div>
      </div>
    </Section>
    <Section dark>
      <H2>L&apos;esperienza di <em style={{ fontStyle: "italic", color: T.goldSoft }}>Davide Carrera.</em></H2>
      <P className="opacity-85 max-w-3xl">Davide ha portato il corpo umano ai limiti dell&apos;apnea profonda, dove ogni battito, ogni pensiero, ogni gesto è respiro. Nel percorso Longevity insegna che quelle stesse tecniche — la calma, il controllo, il rilascio — possono essere strumenti quotidiani per chiunque, non solo per atleti.</P>
      <div className="mt-10"><CtaPrimary /></div>
    </Section>
  </Layout>
);

export const DatiV2 = () => (
  <Layout>
    <Section>
      <Eyebrow>I Dati</Eyebrow>
      <H1>Il tuo corpo <em style={{ color: T.gold, fontStyle: "italic" }}>produce dati. Impara a leggerli.</em></H1>
      <div className="grid md:grid-cols-3 gap-6 mt-10">
        {[
          {t:"ANS Control", d:"La variabilità cardiaca (HRV) come misura del recupero e dello stato del sistema nervoso autonomo."},
          {t:"Composizione corporea", d:"Massa magra, massa grassa, idratazione: cosa raccontano davvero i chili sulla bilancia."},
          {t:"VO2 Max", d:"La capacità del tuo corpo di usare ossigeno: il predittore più forte di mortalità dopo l'età."},
        ].map((d,i)=>(
          <div key={i} className="p-8 bg-white shadow border-t-4" style={{ borderColor: T.gold }}>
            <h3 className="text-2xl mb-3 font-semibold" style={serif}>{d.t}</h3>
            <p className="text-sm" style={{ color: T.soft }}>{d.d}</p>
          </div>
        ))}
      </div>
    </Section>

    <Section dark>
      <Eyebrow>I numeri della scienza</Eyebrow>
      <H2>Perché misurare cambia tutto.</H2>
      <div className="grid md:grid-cols-2 gap-6 mt-10">
        <div className="p-8 border-2" style={{ borderColor: T.gold, backgroundColor: "rgba(236,107,31,0.08)" }}>
          <div className="text-6xl font-bold" style={{ ...serif, color: T.goldSoft }}>−14%</div>
          <p className="mt-3 opacity-90">Mortalità per tutte le cause per ogni +1 MET di fitness cardiorespiratoria. Meta-analisi su 3.813.484 persone (Singh et al., 2024, <em>Journal of Sport and Health Science</em>).</p>
        </div>
        <div className="p-8 border-2" style={{ borderColor: T.gold, backgroundColor: "rgba(236,107,31,0.08)" }}>
          <div className="text-6xl font-bold" style={{ ...serif, color: T.goldSoft }}>−16%</div>
          <p className="mt-3 opacity-90">Mortalità cardiovascolare per ogni +1 MET. Fonti autorevoli: PubMed, American Heart Association, JAMA, The Lancet, BJSM.</p>
        </div>
      </div>
      <p className="text-sm opacity-60 italic mt-8">Materiale di supporto scientifico. Non un percorso separato: la misurazione è parte integrante del metodo Longevity.</p>
      <div className="mt-10"><CtaPrimary /></div>
    </Section>
  </Layout>
);

export const AziendeV2 = () => (
  <Layout>
    <Section>
      <Eyebrow>Per Aziende</Eyebrow>
      <H1>La salute delle persone è anche <em style={{ color: T.gold, fontStyle: "italic" }}>una risorsa per l&apos;azienda.</em></H1>
      <div className="grid md:grid-cols-2 gap-10 mt-10 items-center">
        <div>
          <P>Un percorso di wellbeing aziendale che non si limita al solito corso motivazionale. Portiamo dentro l&apos;organizzazione un metodo scientifico: movimento, alimentazione, respirazione, gestione dello stress, lettura dei dati fisiologici.</P>
          <P>I collaboratori imparano a leggere il proprio corpo — energia, sonno, concentrazione. L&apos;azienda ottiene team più presenti, meno assenteismo, employer branding.</P>
        </div>
        <div><img src={IMG.aziende} alt="Colleghi 40-55 anni in un momento di pausa in ufficio" className="w-full shadow-2xl" /></div>
      </div>
    </Section>

    <Section dark>
      <Eyebrow>Pacchetti</Eyebrow>
      <H2>Scalabili in base al numero di collaboratori.</H2>
      <div className="grid md:grid-cols-3 gap-5 mt-10">
        {[
          {n:"Starter", pax:"10-50", desc:"PMI e team dedicati. Assessment iniziale, 8 settimane online, 1 giornata finale in natura."},
          {n:"Growth", pax:"50-150", desc:"Aziende medie. Coach dedicato per team, sessioni 1-to-1 opzionali, dashboard KPI.", h:true},
          {n:"Enterprise", pax:"150+", desc:"Banche e multinazionali. Progetto pilota custom, multi-sede, account manager dedicato."},
        ].map((p,i)=>(
          <div key={i} className={`p-6 border-2 ${p.h ? "scale-[1.02]" : ""}`} style={{ borderColor: p.h ? T.gold : "rgba(255,255,255,0.2)", backgroundColor: p.h ? "rgba(236,107,31,0.1)" : "rgba(255,255,255,0.03)" }}>
            <h3 className="text-2xl font-light mb-1" style={{ ...serif, color: T.goldSoft }}>{p.n}</h3>
            <p className="text-xs uppercase tracking-widest font-bold mb-4" style={{ color: T.goldSoft }}>{p.pax} dipendenti</p>
            <p className="text-sm opacity-85">{p.desc}</p>
          </div>
        ))}
      </div>
      <div className="mt-12 text-center">
        <Link to="/v2/iscriviti" className="inline-flex items-center gap-3 px-8 py-4 text-sm tracking-[2px] uppercase font-bold" style={{ backgroundColor: T.gold, color: "#fff" }}>Parliamone insieme <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </Section>
  </Layout>
);

export const AcademyV2 = () => (
  <Layout>
    <Section>
      <Eyebrow>Chi siamo</Eyebrow>
      <H1>Una Academy nata per rendere <em style={{ color: T.gold, fontStyle: "italic" }}>comprensibile la scienza del corpo umano.</em></H1>
      <P className="max-w-3xl">Un progetto che unisce attività fisica, respirazione, nutrizione, fisiologia e tecnologia in un&apos;unica community scientifica. Nessuna moda, nessun guru: metodo verificabile e persone reali.</P>
    </Section>
    <Section dark>
      <Eyebrow>Il team</Eyebrow>
      <H2>Chi guida il metodo.</H2>
      <div className="grid md:grid-cols-3 gap-8 mt-10">
        {[
          {n:"Dott. Jacopo Querci", r:"Nutrizione & BioFitness", b:"Biologo specializzato in nutrizione e biofitness. Sviluppa i protocolli scientifici del metodo."},
          {n:"Davide Carrera", r:"Respirazione & Stress Management", b:"Campione del mondo di apnea. Guida le tecniche di gestione dello stress e regolazione del sistema nervoso."},
          {n:"Prof. Andrea Corradini", r:"Direzione scientifica", b:"Direzione scientifica del progetto Longevity Academy Italia."},
        ].map((m,i)=>(
          <div key={i} className="text-center">
            <div className="w-32 h-32 mx-auto mb-4 rounded-full flex items-center justify-center text-4xl font-bold" style={{ backgroundColor: T.gold, color: "#fff", ...serif, boxShadow: `0 0 0 4px rgba(255,255,255,0.1)` }}>{m.n.split(" ").map(s=>s[0]).slice(0,2).join("")}</div>
            <h3 className="text-xl font-semibold" style={{ ...serif, color: T.goldSoft }}>{m.n}</h3>
            <p className="text-xs uppercase tracking-widest my-2 opacity-70">{m.r}</p>
            <p className="text-sm opacity-80">{m.b}</p>
          </div>
        ))}
      </div>
    </Section>
    <Section>
      <Eyebrow>Community</Eyebrow>
      <H2>Un raduno annuale <em style={{ fontStyle: "italic", color: T.gold }}>in Toscana.</em></H2>
      <div className="grid md:grid-cols-2 gap-10 items-center mt-8">
        <div>
          <P className="max-w-xl">Ogni iscritto Longevity Academy ha accesso gratuito al <strong>Raduno Annuale in Toscana</strong> (escluse spese di vitto e alloggio): un&apos;occasione per ritrovare dal vivo la community e vivere la scienza della longevità in un contesto unico — attività fisica all&apos;aperto, respirazione nella natura, workshop scientifici.</P>
          <ul className="mt-6 space-y-2 text-sm" style={{ color: T.soft }}>
            {["Colline toscane, natura autentica","Attività fisica guidata e movimento outdoor","Workshop scientifici con il team","Community di persone reali, non un ritiro anonimo"].map((t,i)=>(
              <li key={i} className="flex gap-2 items-start"><Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: T.gold }} /><span>{t}</span></li>
            ))}
          </ul>
        </div>
        <div className="relative">
          <img
            src={IMG.toscana}
            alt="Raduno annuale in Toscana — persone in attività fisica nella natura toscana"
            className="w-full h-[420px] object-cover shadow-2xl"
            data-testid="retreat-toscana-image"
          />
          <div className="absolute -top-3 -right-3 px-4 py-2 text-[11px] uppercase tracking-[3px] font-bold shadow-lg" style={{ backgroundColor: T.gold, color: "#fff" }}>Toscana · Raduno annuale</div>
        </div>
      </div>
      <div className="mt-12"><CtaPrimary /></div>
    </Section>
  </Layout>
);

const FAQS = [
  {q:"È una dieta?", a:"No. Longevity Academy non ti consegna un piano alimentare da eseguire. Ti insegna a comprendere come il tuo corpo usa i nutrienti, così puoi fare scelte consapevoli in autonomia — non seguire regole imposte."},
  {q:"Devo essere allenato?", a:"No. Il percorso parte dalle fondamenta. Chi non fa sport da anni è, scientificamente, la persona che ha più da guadagnare da questo tipo di percorso."},
  {q:"Fate diagnosi mediche?", a:"No. Longevity Academy è un percorso educativo e di consapevolezza, non sostituisce il medico. Ti insegniamo a leggere segnali e dati, non a diagnosticare patologie."},
  {q:"Quanto dura il percorso?", a:"8 settimane con 2 incontri live a settimana, 45 minuti ciascuno."},
  {q:"Devo essere presente in orario o è tutto registrato?", a:"Gli incontri sono live, in orario serale. Se non riesci ad essere presente, hai accesso alla registrazione. La partecipazione live è consigliata per fare domande in tempo reale."},
  {q:"Cosa ottengo alla fine?", a:"Un metodo tuo. Sapere come leggere il tuo corpo, quali dati misurare, come costruire nutrizione, movimento e recupero in modo sostenibile nel tempo. Non un pacchetto pronto — una consapevolezza che resta."},
  {q:"È adatto a chi ha già una malattia cronica?", a:"Il percorso non sostituisce la cura medica. Chi ha patologie in corso può partecipare, ma è consigliato farlo dopo aver parlato con il proprio medico curante."},
  {q:"Quanto costa?", a:"Il prezzo del percorso viene comunicato nella pagina di iscrizione. Per aziende, il preventivo è personalizzato in base al numero di partecipanti."},
];

export const FaqV2 = () => {
  React.useEffect(() => {
    const schema = { "@context": "https://schema.org", "@type": "FAQPage",
      "mainEntity": FAQS.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) };
    const s = document.createElement("script"); s.type = "application/ld+json"; s.text = JSON.stringify(schema); document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, []);
  return (
    <Layout>
      <Section>
        <Eyebrow>FAQ</Eyebrow>
        <H1>Domande frequenti.</H1>
        <div className="mt-10 space-y-6">
          {FAQS.map((f,i)=>(
            <details key={i} className="p-6 bg-white shadow border-l-4 group" style={{ borderColor: T.gold }} data-testid={`faq-${i}`}>
              <summary className="font-semibold text-lg cursor-pointer" style={serif}>{f.q}</summary>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: T.soft }}>{f.a}</p>
            </details>
          ))}
        </div>
        <div className="mt-14 text-center"><CtaPrimary /></div>
      </Section>
    </Layout>
  );
};

export const IscrivitiV2 = () => (
  <Layout minimal>
    <Section className="!py-24">
      <div className="max-w-3xl mx-auto text-center">
        <Eyebrow>Iscrizione</Eyebrow>
        <H1>Il tuo percorso comincia da una domanda.</H1>
        <P className="max-w-2xl mx-auto"><em>&quot;Quanto conosco davvero il mio corpo?&quot;</em></P>
        <P>8 settimane. 2 incontri live a settimana. Formazione, consapevolezza, dati e supervisione. Non un pacchetto pronto: un metodo che costruisci tu.</P>
        <div className="mt-10 p-8 bg-white border-t-4 shadow-2xl" style={{ borderColor: T.gold }}>
          <p className="text-xs uppercase tracking-widest mb-3" style={{ color: T.gold }}>Prossimo passo</p>
          <p className="text-lg mb-6" style={serif}>Lasciaci il tuo contatto: ti scriviamo con calendario, prezzo e modalità di iscrizione.</p>
          <form action="mailto:info@longevityacademy.it" method="post" encType="text/plain" className="space-y-4">
            <input required name="Nome" placeholder="Nome e cognome" className="w-full px-4 py-3 border" style={{ borderColor: T.line }} data-testid="input-nome" />
            <input required type="email" name="Email" placeholder="Email" className="w-full px-4 py-3 border" style={{ borderColor: T.line }} data-testid="input-email" />
            <input name="Telefono" placeholder="Telefono (opzionale)" className="w-full px-4 py-3 border" style={{ borderColor: T.line }} />
            <textarea name="Note" rows="3" placeholder="Note / obiettivi" className="w-full px-4 py-3 border" style={{ borderColor: T.line }} />
            <button type="submit" className="w-full py-4 text-sm tracking-widest uppercase font-bold" style={{ backgroundColor: T.gold, color: "#fff" }} data-testid="submit-iscriviti">
              Iscriviti alla Longevity Academy
            </button>
          </form>
        </div>
        <p className="text-xs mt-6 opacity-60 italic">Il prezzo del percorso ti viene comunicato dopo la richiesta di contatto, insieme al calendario dell&apos;edizione più vicina.</p>
      </div>
    </Section>
  </Layout>
);
